import { Component, computed, inject, input, signal, effect } from '@angular/core';
import { Branch, LocationTheftReport } from '../../../types';
import { catchError, finalize, retry } from 'rxjs/operators';
import { of } from 'rxjs';
import { BranchService } from '../../../service/branch.service';
import { ColDef, GridOptions } from 'ag-grid-community';
import { defaultTableDefOptions } from '../../../helpers';
import { CellReportPortalActionsComponent } from './cell-report-portal-actions/cell-report-portal-actions.component';
import { CellReportPortalTypeComponent } from './cell-report-portal-type/cell-report-portal-type.component';
import { linkedQueryParam } from 'ngxtension/linked-query-param';
import { clamp } from 'lodash';

@Component({
  selector: 'app-report-portal-branch-card',
  templateUrl: './report-portal-branch-card.component.html',
  styleUrl: './report-portal-branch-card.component.scss',
  standalone: false,
})
export class ReportPortalBranchCardComponent {
  private branchService = inject(BranchService);

  readonly pageDaysBack = linkedQueryParam<number>('since_days', {
    parse: (value) => clamp(parseInt(value ?? '1', 10), 7, 365),
  });

  public branch = input.required<Branch>();
  public dateFrom = input<Date | undefined>(undefined);
  public dateTo = input<Date | undefined>(undefined);

  public isLoading = signal<boolean>(false);
  public locations = signal<LocationTheftReport[] | undefined>(undefined);

  public tableLocations = computed(() =>
    this.locations().map((l) => ({
      ...l,
      most_recent_date_to: new Date(l.most_recent_date_to),
    })),
  );

  public readonly tableDef: GridOptions = {
    ...defaultTableDefOptions(),
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 50],
  };

  public readonly columnDefs: ColDef<LocationTheftReport>[] = [
    {
      field: 'external_location_id',
      headerName: 'External ID',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: false,
      minWidth: 140,
      width: 140,
      tooltipField: 'external_location_id',
    },
    {
      field: 'location_name',
      headerName: 'Location Name',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: false,
      tooltipField: 'location_name',
      minWidth: 180,
      width: 180,
    },
    {
      field: 'theft_count',
      headerName: 'Incidents Found',
      filter: 'agNumberColumnFilter',
      sortable: false,
      minWidth: 195,
      width: 195,
      cellRenderer: CellReportPortalTypeComponent,
    },
    {
      field: 'qa_theft_count',
      headerName: 'Incidents in QA',
      cellDataType: 'number',
      filter: 'agNumberColumnFilter',
      sortable: false,
      headerTooltip: 'Incidents in QA',
    },
    {
      field: 'repeat_offenders_count',
      headerName: 'Repeat Offender/s',
      headerTooltip: 'Repeat Offender/s',
      cellDataType: 'number',
      filter: 'agNumberColumnFilter',
      sortable: false,
    },
    {
      field: 'most_recent_date_to',
      headerName: 'Review Period End Date',
      cellDataType: 'date',
      minWidth: 150,
      width: 150,
      filter: 'agDateColumnFilter',
      headerTooltip: 'Review Period End Date',
    },
    {
      cellClass: ['inline-flex', 'items-center', 'justify-end'],
      sortable: false,
      cellRenderer: CellReportPortalActionsComponent,
      minWidth: 320,
      width: 320,
    },
  ];

  public theftsFound = computed(() =>
    this.locations()
      .filter((d) => d.theft_found === true)
      .length.toString(),
  );
  public noTheftsFound = computed(() =>
    this.locations()
      .filter((d) => d.theft_found === false)
      .length.toString(),
  );

  constructor() {
    effect(() => {
      const branch = this.branch();
      const dateFrom = this.dateFrom();
      const dateTo = this.dateTo();
      if (branch && dateFrom instanceof Date && dateTo instanceof Date) {
        this.loadBranchLocationsReviews(branch.id, dateFrom, dateTo);
      }
    });
  }

  private loadBranchLocationsReviews(branchId: number, dateFrom?: Date, dateTo?: Date) {
    this.isLoading.set(true);
    this.branchService
      .getBranchLocationsReviewsWithRange(branchId, dateFrom, dateTo)
      .pipe(
        retry(3),
        catchError((err) => {
          console.error('Failed to fetch branch locations reviews after 3 attempts', err);
          return of([]);
        }),
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe((data) => {
        this.locations.set(data);
      });
  }
}
